import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {useApplicationContext} from "../../ApplicationContext";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {CalendarEmployeeDto, GetUserData} from "../../apiClient/Api";
import {DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridToolbarQuickFilter, Toolbar} from "@mui/x-data-grid";
import {format, parseISO} from "date-fns";
import {
    CheckCircle,
    Edit,
    UploadFile,
    Lock,
    LockOpen,
    CreateNewFolder,
    PersonAdd,
    MarkEmailRead,
    MarkEmailUnread, InfoRounded
} from "@mui/icons-material";
import {Axios, AxiosError} from "axios";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {Alert, Autocomplete, Box, Button, Chip, Stack, TableContainer, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions} from "@mui/material";
import {AddUserComponent} from "../../Components/Admin/User/AddUserComponent";
import Typography from "@mui/material/Typography";
import {DeleteUserDialog} from "../../Components/Admin/User/DeleteUserDialog";
import Paper from "@mui/material/Paper";
import {AdvancedInfoDialog} from "../../Components/Admin/User/AdvancedInfoDialog";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faEnvelopeCircleCheck, faEnvelopeOpen, faHeadset, faTag } from '@fortawesome/pro-solid-svg-icons'
import {EUserGroup} from "../../apiClient/Api";

export const AdminUsers = () => {
    useDocumentTitle("base.pages.users")
    const { t } = useTranslation();
    const context = useApplicationContext();
    const navigate = useNavigate();
    const [users, setUsers] = useState<GetUserData[]>([]);
    const [initialized, setInitialized] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [newUserOpen, setNewUserOpen] = useState(false);
    const [deleteUserID, setDeleteUserId] = useState<undefined | string>( undefined);
    const [advancedInfoUserId, setAdvancedInfoUserId] = useState<undefined | string>( undefined);
    const [resendUserId, setResendUserId] = useState<undefined | string>(undefined);
    const [resendUserEmail, setResendUserEmail] = useState<string>("");
    const [labelUserEditId, setLabelUserEditId] = useState<undefined | string>(undefined);
    const [labelUserValue, setLabelUserValue] = useState<string>("");
    const [labelEmployees, setLabelEmployees] = useState<CalendarEmployeeDto[]>([]);
    const [labelEmployeesLoading, setLabelEmployeesLoading] = useState(false);
    const [labelEmployeesError, setLabelEmployeesError] = useState<string | undefined>(undefined);

    const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
    const closeNewUserCallback = useCallback((created: boolean) => {
        setNewUserOpen(false);
        setSelectedId(undefined)
        if(created){
            updateUserData();
        }
    }, [newUserOpen])

    const advancedInfoClosed = useCallback(() =>{
        setAdvancedInfoUserId(undefined);
    } , [advancedInfoUserId])

    const userDeletedCallback = useCallback((deleted: boolean) =>{
        setDeleteUserId(undefined);
        if(deleted){
            updateUserData();
        }
    } , [deleteUserID])

    const EditToolbar = () => (
        <Toolbar style={{display: "block", minHeight: "auto", padding: 0, borderBottom: "none"}}>
            <Grid container sx={{marginTop: 2, marginBottom: 1}}>
                <Grid size={{md: 8}}>
                    <Button onClick={() => setNewUserOpen(true)} startIcon={<PersonAdd/>}>Neuen Nutzer anlegen</Button>
                </Grid>
                <Grid size={{md: 4}}>
                    <GridToolbarQuickFilter/>
                </Grid>
            </Grid>
        </Toolbar>
    )

    const updateUserData = () => {
        setIsFetching(true)
        context.api.user.userListUsers().then(usersdata => {
            setUsers(usersdata.data);
           setIsFetching(false)
        }).catch(context.apiError)
    }


    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
            updateUserData()
        }
    }, [initialized, users])

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            renderCell: (params) => {
                return <Button
                    variant="text"
                    size="large"
                    color="primary"
                    target="_top"
                    rel="noopener noreferrer"
                    href={`mailto:${params.row.eMail}`}
                >
                    <Typography variant="button" style={{ fontSize: '0.69rem' }}>
                        {params.row.name || ''}
                    </Typography>
                </Button>
            }

        },
        {field: "eMail", headerName: "Mail", flex: 1},
        {
            field: "labelUser",
            headerName: t('adminUsers.labelUser'),
            width: 150,
            valueFormatter: (value: string | null | undefined) => value || "–",
        },
        {
            field: "userGroup",
            type: "singleSelect",
            valueOptions: (params) => {
                return [{value: 0, label: "Super Admin"},
                    {value: 1, label: "Admin"},
                    {value: 2, label: "Entwickler"},
                    {value: 3, label: "Büro"},
                    {value: 4, label: "Kunde"}]
            },
            headerName: "Nutzergruppe",
            valueFormatter: (value: number | undefined) => {
                if (value != undefined) {
                    switch (value) {
                        case 0:
                            return "Super Admin";
                        case 1:
                            return "Admin";
                        case  2:
                            return "Entwickler";
                        case 3:
                            return "Büro";
                        case 4:
                            return "Kunde";
                    }
                }
                return "Unbekannt"
            },
            width: 130
        },
        {
            field: "lastLogin",
            width: 200,
            headerName: "Letzter Login",
            valueFormatter: (value: string | undefined) => {
                if (value == undefined) {
                    return "Nicht eingeloggt"
                }
                const date = parseISO(value.toString()) // not MM-DD-YY
                return format(date, "dd.MM.yyyy hh:mm")
            }

        },
        {
            field: 'actions',
            type: 'actions',
            width: 250,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem 
                    label={t('adminUsers.showSupportTickets')} 
                    onClick={() => {
                        navigate(`/tickets?filterEmail=${encodeURIComponent(params.row.eMail)}`);
                    }} 
                    icon={<Tooltip title={t('adminUsers.showSupportTickets')}><FontAwesomeIcon icon={faHeadset}/></Tooltip>}
                    showInMenu={false}
                />,
                <GridActionsCellItem 
                    label={t('adminUsers.userInformation')}  
                    onClick={() => {
                        setAdvancedInfoUserId(params.row.id);
                    }} 
                    icon={<Tooltip title={t('adminUsers.userInformation')}><InfoRounded/></Tooltip>}
                    showInMenu={false}
                />,
                <GridActionsCellItem 
                    label={t('adminUsers.editUser')} 
                    onClick={() => {
                        setSelectedId(params.row.id);
                        setNewUserOpen(true);
                    }} 
                    icon={<Tooltip title={t('adminUsers.editUser')}><Edit/></Tooltip>}
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    label={t('adminUsers.setLabelUser')}
                    disabled={(context.userData?.userGroup ?? EUserGroup.CUSTOMER) > EUserGroup.ADMIN}
                    onClick={() => {
                        setLabelUserEditId(params.row.id);
                        setLabelUserValue(params.row.labelUser ?? '');
                    }}
                    icon={<Tooltip title={t('adminUsers.setLabelUser')}><FontAwesomeIcon icon={faTag}/></Tooltip>}
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    label={!params.row.locked ? t('adminUsers.lockUser') : t('adminUsers.unlockUser')}
                    onClick={() => {
                        context.api.user.userLockUser(params.row.id).then(async (data) => {
                            if (data.data) {
                                await updateUserData();
                            }
                        }).catch(context.apiError)
                    }} 
                    icon={<Tooltip title={!params.row.locked ? t('adminUsers.lockUser') : t('adminUsers.unlockUser')}>{!params.row.locked ? <Lock/> : <LockOpen/>}</Tooltip>}
                    showInMenu={false}
                />,

                <GridActionsCellItem 
                    label={t('adminUsers.deleteUser')} 
                    disabled={(context.userData!.userGroup ?? 4) >= 2} 
                    onClick={() => {
                        setDeleteUserId(params.row.id);
                    }} 
                    icon={<Tooltip title={t('adminUsers.deleteUser')}><DeleteIcon/></Tooltip>}
                    showInMenu={false}
                />,
                <GridActionsCellItem 
                    label={params.row.lastLogin == undefined ? t('adminUsers.sendLoginData') : t('adminUsers.userAlreadyLoggedIn')} 
                    onClick={() => {
                        if(params.row.lastLogin == undefined){
                            // Nutzer war noch nie eingeloggt - direkt senden
                            context.api.user.userResendFirstLogin(params.row.id).then(u => {
                                if(u.data)
                                  alert(t('adminUsers.dataSentToUser'))
                            }).catch(context.apiError)
                        } else {
                            // Nutzer war bereits eingeloggt - Bestätigung erforderlich
                            setResendUserId(params.row.id);
                            setResendUserEmail(params.row.eMail);
                        }
                    }} 
                    icon={
                        <Tooltip title={params.row.lastLogin == undefined ? t('adminUsers.sendLoginData') : t('adminUsers.userAlreadyLoggedIn')}>
                            {params.row.lastLogin == undefined ? 
                                (params.row.sentWelcomeMail == undefined ? 
                                    <FontAwesomeIcon icon={faEnvelope}/> : 
                                    <FontAwesomeIcon icon={faEnvelopeCircleCheck}/>) : 
                                <FontAwesomeIcon icon={faEnvelopeOpen}/>}
                        </Tooltip>
                    }
                    showInMenu={false}
                />,
            ]
        }
    ]

    const handleResendConfirm = () => {
        if(resendUserId) {
            // Nutzer war bereits eingeloggt - verwende ResendPasswordReset
            context.api.user.userResendPasswordReset(resendUserId).then(u => {
                if(u.data)
                    alert(t('adminUsers.dataSentToUser'))
                setResendUserId(undefined);
                setResendUserEmail("");
            }).catch(context.apiError)
        }
    };

    const handleResendCancel = () => {
        setResendUserId(undefined);
        setResendUserEmail("");
    };

    /**
     * Lädt die Labelwin-Mitarbeiter, sobald der Dialog aufgeht — nicht beim Seitenaufbau. Der Abruf
     * geht über LabelConnect an das ERP; die Benutzerliste soll nicht davon abhängen.
     */
    useEffect(() => {
        if (labelUserEditId === undefined || labelEmployees.length > 0 || labelEmployeesLoading) return;

        setLabelEmployeesLoading(true);
        setLabelEmployeesError(undefined);
        context.api.labelConnect.labelConnectGetEmployees()
            .then((response) => setLabelEmployees(response.data ?? []))
            .catch((e) => {
                // Bewusst nicht context.apiError: eine abgeschaltete oder gestörte Anbindung ist hier
                // ein erwarteter Zustand. Der Dialog fällt dann auf Freitext zurück, statt den
                // Bediener mit einer Fehlermeldung stehen zu lassen.
                const message = typeof e?.response?.data === 'string'
                    ? e.response.data
                    : e?.response?.data?.message ?? e?.message;
                setLabelEmployeesError(message ?? t('adminUsers.labelUserListFailed'));
            })
            .finally(() => setLabelEmployeesLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labelUserEditId]);

    /**
     * Welcher LabelBenutzer hängt schon an welchem Support-Konto? Ohne diese Anzeige vergibt man
     * denselben Labelwin-Mitarbeiter zweimal — und der Server lehnt dann beide Kalender ab, weil
     * die Zuordnung nicht mehr eindeutig ist.
     */
    const labelUserAssignments = useMemo(() => {
        const map = new Map<string, string>();
        for (const user of users) {
            const key = user.labelUser?.trim().toLowerCase();
            if (key && user.id !== labelUserEditId) map.set(key, user.name ?? user.eMail ?? '');
        }
        return map;
    }, [users, labelUserEditId]);

    /** Nur Mitarbeiter mit gepflegtem LabelBenutzer taugen als Auswahl. */
    const labelUserOptions = useMemo(
        () => labelEmployees.filter((employee) => !!employee.labelUser?.trim()),
        [labelEmployees]);

    const labelEmployeesWithoutUser = labelEmployees.length - labelUserOptions.length;

    const handleLabelUserSave = () => {
        if (!labelUserEditId) return;
        context.api.user
            .userSetLabelUser(labelUserEditId, { labelUser: labelUserValue.trim() || null })
            .then(() => {
                setLabelUserEditId(undefined);
                updateUserData();
            })
            .catch(context.apiError);
    };

    return <>
        <AddUserComponent open={newUserOpen} closeComponent={closeNewUserCallback} userId={selectedId} />
        <DeleteUserDialog userDeleted={userDeletedCallback} id={deleteUserID} />
        <AdvancedInfoDialog advancedInfoClosed={advancedInfoClosed} id={advancedInfoUserId} />
        
        <Dialog open={labelUserEditId !== undefined} onClose={() => setLabelUserEditId(undefined)} maxWidth="xs" fullWidth>
            <DialogTitle>{t('adminUsers.setLabelUser')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>{t('adminUsers.labelUserHint')}</DialogContentText>

                {labelEmployeesError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('adminUsers.labelUserListUnavailable')} {labelEmployeesError}
                    </Alert>
                )}

                {/*
                  * Auswahl aus Labelwin statt Abtippen: ein Tippfehler im LabelBenutzer fällt sonst
                  * erst auf, wenn der Kalender leer bleibt. freeSolo bleibt trotzdem an — ist die
                  * Anbindung gestört, muss man den Namen weiterhin von Hand setzen können.
                  */}
                <Autocomplete
                    freeSolo
                    autoHighlight
                    selectOnFocus
                    handleHomeEndKeys
                    openOnFocus
                    options={labelUserOptions}
                    loading={labelEmployeesLoading}
                    inputValue={labelUserValue}
                    onInputChange={(_, value) => setLabelUserValue(value)}
                    onChange={(_, value) =>
                        setLabelUserValue(typeof value === 'string' ? value : (value?.labelUser ?? ''))}
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : (option.labelUser ?? '')}
                    filterOptions={(options, state) => {
                        const needle = state.inputValue.trim().toLowerCase();
                        if (!needle) return options;
                        return options.filter((option) =>
                            (option.labelUser ?? '').toLowerCase().includes(needle)
                            || (option.name ?? '').toLowerCase().includes(needle)
                            || (option.shortCode ?? '').toLowerCase().includes(needle)
                            || String(option.employeeId ?? '').includes(needle));
                    }}
                    renderOption={(props, option) => {
                        const { key, ...liProps } = props as any;
                        const assignedTo = labelUserAssignments.get((option.labelUser ?? '').trim().toLowerCase());

                        return (
                            <Box component="li" key={key} {...liProps}>
                                <Stack sx={{ width: '100%' }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {option.labelUser}
                                        </Typography>
                                        {assignedTo && (
                                            <Chip size="small" color="warning" variant="outlined"
                                                  label={t('adminUsers.labelUserAlreadyAssigned', { name: assignedTo })}/>
                                        )}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.name} · {t('adminUsers.labelUserEmployeeId')} {option.employeeId}
                                        {option.mailInternal ? ` · ${option.mailInternal}` : ''}
                                    </Typography>
                                </Stack>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus
                            size="small"
                            label={t('adminUsers.labelUser')}
                            placeholder="z. B. treichert"
                            helperText={
                                labelEmployeesLoading
                                    ? t('adminUsers.labelUserLoading')
                                    : labelEmployees.length > 0
                                        ? t('adminUsers.labelUserOptionCount', {
                                            count: labelUserOptions.length,
                                            withoutUser: labelEmployeesWithoutUser,
                                        })
                                        : t('adminUsers.labelUserFreeText')
                            }
                            onKeyDown={(e) => {
                                // Enter darf erst speichern, wenn die Vorschlagsliste zu ist — sonst
                                // schluckt der Dialog die Auswahl.
                                if (e.key === 'Enter' && !(e.target as HTMLElement).getAttribute('aria-expanded')?.includes('true')) {
                                    handleLabelUserSave();
                                }
                            }}
                        />
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setLabelUserEditId(undefined)} color="inherit">
                    {t('adminUsers.cancel')}
                </Button>
                <Button onClick={handleLabelUserSave} color="primary" variant="contained">
                    {t('adminUsers.save')}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={resendUserId !== undefined} onClose={handleResendCancel}>
            <DialogTitle>{t('adminUsers.resendConfirmTitle')}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('adminUsers.resendConfirmMessage', { email: resendUserEmail })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleResendCancel} color="inherit">
                    {t('adminUsers.cancel')}
                </Button>
                <Button onClick={handleResendConfirm} color="primary" variant="contained" autoFocus>
                    {t('adminUsers.send')}
                </Button>
            </DialogActions>
        </Dialog>
        <TableContainer component={Paper} style={{margin: 4}}>
            <DataGrid
                showToolbar
                slots={{
                    toolbar: EditToolbar,
                }}
                initialState={{
                    filter: {
                        filterModel: {
                            items: [
                                {id: 1, field: "userGroup", operator: 'is', value: 4}
                            ]
                        }
                    },
                    sorting: {
                        sortModel: [
                            {
                                field: 'lastLogin',
                                sort: 'desc',
                            },
                        ],
                    },
                }}

                rows={users}
                columns={columns}
                loading={isFetching}
                autoHeight={true}></DataGrid>
        </TableContainer>
    </>
}
